import { CustomTooltip } from '@/shared/ui/components';
import { CONFLICT_USER_COLORS } from '@/shared/config';
import { generateFullName } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { useWarningLine } from './useWarningLine';
import type { WarningLineProps } from './warningLine.types';

const EMPTY_ARRAY: any[] = [];

const WarningLine = ({
  conflictUsers = EMPTY_ARRAY,
  isNotCanEdit = false,
  isEventDetailsPage = false,
  isEditReportPage = false,
  isEventsPage = false,
  isReportsPage = false,
  hasSmallPadding = false,
  limitUserCount = 5,
  id = '',
}: WarningLineProps) => {
  const {
    shouldShowConflictNotification,
    generateConflictNotification,
    hasConflictNotification,
    hasBlueBackgroundColor,
  } = useWarningLine({ conflictUsers, isNotCanEdit, isEventDetailsPage, isEditReportPage });

  /**
   * Renders the initials badge for another user concurrently working on the same study. Takes the leading character of the conflicting user's first and last name, picks a background colour by indexing CONFLICT_USER_COLORS with twice the first name's leading char code, and wraps the badge in a tooltip carrying the full name unless the reports page is active.
   *
   * @param conflictUser - The conflicting user, read for firstName, lastName and token.
   * @returns The tooltipped conflict-user icon.
   */
  const generateConflicUserIcon = (conflictUser: any) => {
    const { firstName, lastName, token } = conflictUser;
    const firstCharacterOfFirstName = firstName.charAt(0);
    const firstCharacterOfLastName = lastName.charAt(0);
    const charactersInHex = firstName.charCodeAt(0) + firstName.charCodeAt(0);
    const backgroundColor = CONFLICT_USER_COLORS[charactersInHex % CONFLICT_USER_COLORS.length];
    return (
      <CustomTooltip
        title={!isReportsPage ? generateFullName(conflictUser) : undefined}
        placement="bottom"
      >
        <div
          id={`conflict-user-icon-${token}`}
          className="conflict-user-icon"
          style={{ backgroundColor }}
        >
          <span className="name">{`${firstCharacterOfFirstName}${firstCharacterOfLastName}`}</span>
        </div>
      </CustomTooltip>
    );
  };

  return (
    <div
      id={id}
      className={classNames(
        'info-warning',
        hasConflictNotification ? 'flex-row__ic__jb' : 'flex-row__ic__je bg-transparent',
        hasSmallPadding ? '--has-small-padding' : '--has-no-padding',
        isEventsPage || isReportsPage ? '--has-small-height' : '',
        hasBlueBackgroundColor ? '--has-blue-background' : '',
      )}
    >
      {hasConflictNotification && shouldShowConflictNotification && (
        <span className="text">{generateConflictNotification()}</span>
      )}

      <div className="avatar flex-row__ic">
        {conflictUsers.length <= limitUserCount ? (
          _.map(conflictUsers, (conflictUser) => generateConflicUserIcon(conflictUser))
        ) : (
          <>
            {_.map(conflictUsers.slice(0, limitUserCount - 1), (conflictUser) =>
              generateConflicUserIcon(conflictUser),
            )}

            <div className="conflict-user-icon" style={{ backgroundColor: '#C6CBD1' }}>
              <span className="name">{`+${conflictUsers.length - (limitUserCount - 1)}`}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WarningLine;
